// AgentTools the main chat exposes so any prompt — user-typed, saved
import { mirrorTranscript, refreshMirror } from "./agentTranscript.js";
import { anchorBrief } from "./investigationGraphRun.js";
// slash command, system prompt — can delegate work to sub-agents.
//
// Two tools:
//   spawn_subagent : one focused task, optionally with structured output
//   spawn_batch    : same task fanned out across a list of items
//
// Both resolve `tools[]` against the chat's tool registry by name —
// the model picks which of the parent's tools the child can use.
// This keeps the toolset auditable from the LLM transcript instead of
// hidden behind a closure.
//
// Progress streams up through pi-agent-core's `onUpdate` partial-
// result mechanism: each tool emits `{ details: <state-snapshot> }`
// while running, and the ChatConsole reducer merges those into the
// live tool node. Throttled to ~80ms to keep React happy when a
// dozen children are streaming tokens.

import { Type } from "@earendil-works/pi-ai";
import { runSubAgent, startSubAgent, runWithConcurrency } from "./subAgent.js";
import { getSchemaExamples } from "./schemaSearch.js";
import { getResult, recordResult } from "./toolResultStore.js";
import { resolveStoredOrRehydrate, createRehydrator } from "./toolResultRehydrate.js";
import { createChildHandleRegistry } from "./childHandleRegistry.js";
import { instrumentSteer } from "./steerEvents.js";
import { createSupervisorTools } from "./supervisorTools.js";
import { createPythonTool } from "./pythonTool.js";
import { createExpandPreviewTool } from "./analyticsTool.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import { currentDateSection } from "./promptDate.js";

const EMIT_INTERVAL_MS = 80;
const PREVIEW_TAIL = 200;
const DEFAULT_BATCH_CONCURRENCY = 3;
const MAX_BATCH_ITEMS = 50;

// Recursion is allowed: a sub-agent can spawn grand-children via
// spawn_subagent / spawn_batch. The abort signal cascade carries
// parent STOPs through arbitrary depth (each spawn level passes its
// outer signal into makeChildController; runSubAgent's onOuterAbort
// reaches into pi-agent-core's Agent.abort, which fires the Agent's
// run signal — which IS the outer signal for any tool executing
// inside that child, including its own spawn_* call). To bound
// blast radius from a mistaken fan-out, we cap the number of
// sub-agents in flight across the whole page at any one time.
const MAX_AGENTS_IN_FLIGHT = 25;
let _agentsInFlight = 0;

// ── from_tool_calls staging ───────────────────────────────────────
//
// The parent can hand a slice of a row-shaped result it already
// fetched to a fresh sub-agent. The slice gets staged as a synthetic
// prior tool result in the child's initial message history — as if
// the child itself had just called analytics_query and gotten those
// rows. The child reads, paginates, or pipes the slice into its own
// execute_python the same way it would handle any other tool result;
// no "your input is at id X" contract to teach.
//
// Synthetic messages are marked `synthesized: true` so piChat's
// compactMessages can stub them aggressively under token pressure
// (the originals still live in the parent's toolResultStore).
//
// Refs shape (per parameter doc on each spawn tool):
//   { <child_var_name>: { tool_call_id, slice?: [start, end] } }
//
// The child can find each staged slice by its synthetic
// `_tool_call_id` (echoed in the synthetic tool result) and reach
// for it via:
//   • execute_python with from_tool_calls: { df: "<staged_id>" } —
//     pipes the full slice into a python global without re-emitting.
//   • expand_preview({ tool_call_id: "<staged_id>", offset, limit }) —
//     pages further into the slice if the inlined rows are clipped.
async function buildStagedInputs({ refs, namespaceKey, spawnToolCallId, rehydrate, signal }) {
    if (!refs || typeof refs !== "object") return { messages: [], ids: {}, error: null };
    const messages = [];
    const ids = {};
    const calls = [];
    const results = [];
    for (const [varName, ref] of Object.entries(refs)) {
        if (!ref || typeof ref !== "object") {
            return { messages: [], ids: {}, error: `from_tool_calls.${varName}: expected { tool_call_id, slice? } object` };
        }
        const parentId = typeof ref.tool_call_id === "string" ? ref.tool_call_id.trim() : "";
        if (!parentId) {
            return { messages: [], ids: {}, error: `from_tool_calls.${varName}: missing tool_call_id` };
        }
        // Phase 3: on miss / evicted marker, rehydrate via the
        // original analytics_query spec.
        const { stored, reason } = await resolveStoredOrRehydrate({
            namespaceKey,
            toolCallId: parentId,
            rehydrate,
            signal,
        });
        if (!stored || !Array.isArray(stored.rows)) {
            const tail = reason ? ` (${reason})` : "";
            return { messages: [], ids: {}, error: `from_tool_calls.${varName}: no stored result for ${parentId}${tail}` };
        }
        const total = stored.rows.length;
        let start = 0;
        let end = total;
        if (Array.isArray(ref.slice) && ref.slice.length === 2) {
            const [s, e] = ref.slice;
            start = Math.max(0, Math.floor(Number(s) || 0));
            end = Math.max(start, Math.min(total, Math.floor(Number(e) || 0)));
        } else if (ref.slice != null) {
            return { messages: [], ids: {}, error: `from_tool_calls.${varName}: slice must be [start, end]` };
        }
        const slicedRows = stored.rows.slice(start, end);
        // Synthetic id keyed off the spawn's own tool_call_id so it's
        // unique to this dispatch and easy to trace back.
        const stagedId = `${spawnToolCallId}__staged__${varName}`;
        // Record into the same toolResultStore the child shares with
        // the parent — child's `from_tool_calls` / `expand_preview`
        // look up by id and resolve transparently.
        recordResult(namespaceKey, stagedId, slicedRows, "staged_for_subagent");
        ids[varName] = stagedId;
        // Reconstruct columns from the first row so the synthetic
        // tool result reads like a real analytics_query response.
        const columns = slicedRows.length ? Object.keys(slicedRows[0]) : [];
        const resultPayload = {
            columns,
            rows: slicedRows,
            _tool_call_id: stagedId,
            _staged_from_parent: {
                source_tool_call_id: parentId,
                source_tool: stored.source,
                slice: [start, end],
                total_rows_in_source: total,
            },
        };
        calls.push({
            type: "toolCall",
            id: stagedId,
            name: "analytics_query",
            input: { spec: { _staged: true, var: varName, slice: [start, end] } },
        });
        results.push({
            role: "toolResult",
            toolName: "analytics_query",
            toolCallId: stagedId,
            content: [{ type: "text", text: JSON.stringify(resultPayload) }],
            synthesized: true,
            timestamp: Date.now(),
        });
    }
    if (!calls.length) return { messages: [], ids: {}, error: null };
    messages.push({
        role: "user",
        content: "(Parent agent has staged data for you below. Treat the assistant tool call + tool result that follow as if you made them yourself — they're real rows the parent already fetched and is handing off so you don't have to re-query.)",
        synthesized: true,
        timestamp: Date.now(),
    });
    messages.push({
        role: "assistant",
        content: calls,
        synthesized: true,
        timestamp: Date.now(),
    });
    for (const r of results) messages.push(r);
    return { messages, ids, error: null };
}

// Append a compact "available staged inputs" footer to the child's
// task so the model has a clear mapping between the var names the
// parent chose and the synthetic tool_call_ids it can reach for via
// from_tool_calls / expand_preview.
function appendStagedInputsFooter(task, stagedIds) {
    const entries = Object.entries(stagedIds || {});
    if (!entries.length) return task;
    const lines = entries.map(([varName, id]) => `  • ${varName} → tool_call_id ${id}`);
    return [
        String(task ?? ""),
        "",
        "Staged inputs available in your context:",
        ...lines,
        "",
        "To compute over a staged slice without re-emitting rows: `execute_python` with `from_tool_calls: { <your_var>: \"<that_tool_call_id>\" }`.",
        "To page into a staged slice past what's inlined: `expand_preview({ tool_call_id: \"<that_id>\", offset, limit })`.",
    ].join("\n");
}

// Hand-written fallback spec template. Only used when extracting
// example queries from the live schema fails (unexpected wrapper, no
// examples in the schema, etc.). The real examples — pulled from the
// schema itself — are far more authoritative and are preferred.
const ANALYTICS_QUERY_FALLBACK_TEMPLATE = [
    "Analytics query — fallback spec template (no schema examples available, use this shape):",
    "",
    "```json",
    "{",
    "  \"root\": {\"alias\": \"p\", \"entity\": \"<ENTITY_TYPE_…>\"},",
    "  \"filter\": {\"pred\": {\"field\": {\"stepAlias\": \"p\", \"field\": \"<DIMENSION_FIELD_…>\"}, \"op\": \"OPERATOR_EQ\", \"value\": \"…\"}},",
    "  \"output\": {\"dimensions\": [{\"field\": {\"stepAlias\": \"p\", \"field\": \"<DIMENSION_FIELD_…>\"}, \"outputAlias\": \"col\"}]},",
    "  \"limit\": 500",
    "}",
    "```",
    "",
    "Combinators: `{\"andExpr\": {\"children\": [pred, pred]}}`, `{\"orExpr\": {\"children\": [...]}}`. Ops: OPERATOR_EQ/NE/IN/GT/LT/LIKE/ILIKE.",
    "Use `schema_search` to find entity/field names; copy them into the template; call analytics_query.",
].join("\n");

// Build the analytics-query guidance block by inlining the schema's
// own example queries (preferred) or falling back to the hand-written
// template if extraction fails. Cached: the schema fetch is memoized
// and we hand the same string to every child in a batch so Anthropic's
// prefix cache keeps the cost flat across fan-out.
let _analyticsGuideCache = null;
onAuthIdentityChange(() => { _analyticsGuideCache = null; });
export async function buildAnalyticsGuide() {
    const epoch = getAuthIdentityEpoch();
    if (_analyticsGuideCache) return _analyticsGuideCache;
    let examples = [];
    try {
        examples = await getSchemaExamples();
    } catch (err) {
        console.warn("[subAgentTools] schema examples fetch failed:", err);
    }
    let block;
    if (examples.length) {
        block = [
            "Analytics query — working example specs from this backend's schema (copy the shape, swap fields for your task):",
            "",
            "```json",
            JSON.stringify(examples, null, 2),
            "```",
            "",
            "Rules:",
            "  • Pick a unique `alias` per step; reference it via `stepAlias` everywhere.",
            "  • Operator vocabulary: OPERATOR_EQ, OPERATOR_NE, OPERATOR_IN (value: array), OPERATOR_GT, OPERATOR_LT, OPERATOR_LIKE, OPERATOR_ILIKE.",
            "  • Combine predicates with `{\"andExpr\": {\"children\": [pred, pred]}}` / `{\"orExpr\": ...}`.",
            "  • Array-typed fields (paths, tool-call names) accept only OPERATOR_IN/LIKE/ILIKE.",
            "  • Result shape: `{columns: string[], rows: [{values: any[]}]}` — `columns[i]` pairs with `rows[r].values[i]`.",
            "",
            "Workflow: schema_search for any field/entity names you need → copy a matching example → swap in your names → call analytics_query. If a query fails, read the error and consult the examples above — don't retry the same spec.",
        ].join("\n");
    } else {
        block = ANALYTICS_QUERY_FALLBACK_TEMPLATE;
    }
    if (getAuthIdentityEpoch() !== epoch) {
        const error = new Error("Authentication identity changed while building analytics guidance");
        error.name = "AuthContextChangedError";
        throw error;
    }
    _analyticsGuideCache = block;
    return block;
}

// Children that have an `output_schema` set MUST call `return_result`
// to deliver their answer. Models occasionally write their answer as
// assistant text instead and the run ends with no captured result;
// this short reminder is folded into the system prompt to make the
// expectation impossible to miss.
const RETURN_RESULT_REMINDER =
    "Finishing: when you have your answer, call `return_result` exactly once with the structured value. Do not narrate your answer in assistant text — the tool call is the answer.";

// Default toolkit given to children when `tools` is omitted entirely
// (NOT when it's an explicit empty array — that means "no tools" and
// is respected). Read-only schema discovery + analytics_query +
// execute_python covers the overwhelming majority of investigation
// tasks. Anything not in the registry is silently skipped.
const DEFAULT_CHILD_TOOLKIT = [
    "schema_search",
    "schema_describe_entity",
    "schema_list_entities",
    "analytics_query",
    "execute_python",
    "render_block",
];

// Names that should always be CHILD-scoped, never inherited from
// the parent's tool list. When a parent gives a child spawn_*
// capabilities, the child must subscribe to its own steer-event
// listener set — not the parent's — so a user steer of the parent
// doesn't spuriously interrupt grandchildren the parent's user never
// knew existed.
const CHILD_SCOPED_TOOL_NAMES = new Set([
    "spawn_subagent",
    "spawn_batch",
    "list_subagents",
    "steer_subagent",
    "abort_subagent",
    "await_subagent",
]);

// Build a child-scoped supervision toolkit. Each child gets:
//   • its own rehydrator that walks the CHILD's agent.state.messages
//     when from_tool_calls / expand_preview / staged-input lookups
//     miss the store. Without this, the parent's rehydrator would
//     search the parent's messages for ids the child emitted —
//     guaranteed miss.
//   • child-scoped execute_python and expand_preview (if the parent
//     forwarded those tools) that use the child rehydrator.
//   • if the parent also forwarded spawn_subagent / spawn_batch:
//       — its own childHandleRegistry (grandchildren land here, not
//         the parent's)
//       — its own steer-listener set (only fires when this child
//         is steered, not on user steers of the parent)
//       — child-scoped spawn_subagent / spawn_batch that race against
//         the child's own steer and forward the child rehydrator down
//         to grandchildren
//       — supervisor tools operating on the child's registry
//
// Returns { tools, instrument, childRegistry }. `instrument(child)`
// must be called once the Agent is constructed (typically in
// onChildReady) so the rehydrator and steer-shim can capture the
// child Agent reference. `childRegistry` is non-null only when the
// child got spawn capabilities (otherwise no grandchildren ever land).
//
// Pure passthrough (no overrides at all) when the child doesn't have
// any of execute_python / expand_preview / spawn_*. Cheap — most
// minimal children skip this entirely.
function buildChildSupervision(resolvedChildTools, { getNamespaceKey, scrubbedToolNames }) {
    const hasName = (name) => resolvedChildTools.some((t) => t.name === name);
    const hasPython = hasName("execute_python");
    const hasExpand = hasName("expand_preview");
    const wantsSpawn = hasName("spawn_subagent") || hasName("spawn_batch");

    if (!hasPython && !hasExpand && !wantsSpawn) {
        return { tools: resolvedChildTools, instrument: () => {}, childRegistry: null };
    }

    // Per-agent rehydrator. The closures resolve `childAgent` /
    // `childToolsHolder` lazily — they're undefined / empty at this
    // point, but populated by the time any tool's execute fires
    // (instrument sets childAgent before agent.prompt; childToolsHolder
    // is reassigned synchronously below).
    let childAgent;
    let childToolsHolder = [];
    const childToolRegistry = () => childToolsHolder;
    const childRehydrate = createRehydrator({
        getAgent: () => childAgent,
        getNamespaceKey,
        getToolRegistry: childToolRegistry,
    });

    // Map of name → child-scoped tool replacement. Built up below
    // depending on what the parent forwarded.
    const overrides = new Map();

    // Always rebuild execute_python / expand_preview when the child
    // has them: their rehydrate must point at the CHILD's messages.
    if (hasPython) {
        overrides.set("execute_python", createPythonTool({ getNamespaceKey, rehydrate: childRehydrate }));
    }
    if (hasExpand) {
        overrides.set("expand_preview", createExpandPreviewTool({ getNamespaceKey, rehydrate: childRehydrate }));
    }

    let childRegistry = null;
    let childSteerSubscriber = null;
    if (wantsSpawn) {
        childRegistry = createChildHandleRegistry();
        const subscribeToChildSteer = (cb) => childSteerSubscriber?.subscribe(cb) ?? (() => {});
        // Grandchildren inherit transitively: the child's own
        // state.thinkingLevel was set by startSubAgent from the level we
        // threaded in, so reading it here propagates the depth down the
        // chain without re-plumbing the parent's getter.
        const getChildThinkingLevel = () => childAgent?.state?.thinkingLevel;
        overrides.set("spawn_subagent", createSpawnSubAgentTool({
            toolRegistry: childToolRegistry,
            scrubbedToolNames,
            getNamespaceKey,
            subscribeToParentSteer: subscribeToChildSteer,
            childRegistry,
            rehydrate: childRehydrate,
            getThinkingLevel: getChildThinkingLevel,
        }));
        overrides.set("spawn_batch", createSpawnBatchTool({
            toolRegistry: childToolRegistry,
            scrubbedToolNames,
            getNamespaceKey,
            subscribeToParentSteer: subscribeToChildSteer,
            childRegistry,
            rehydrate: childRehydrate,
            getThinkingLevel: getChildThinkingLevel,
        }));
        for (const t of createSupervisorTools({
            childRegistry,
            subscribeToParentSteer: subscribeToChildSteer,
        })) {
            overrides.set(t.name, t);
        }
    }

    // Strip any tool the parent forwarded that we're replacing,
    // then append the child-scoped versions.
    const filtered = resolvedChildTools.filter((t) => !overrides.has(t.name));
    childToolsHolder = [...filtered, ...overrides.values()];

    return {
        tools: childToolsHolder,
        instrument: (child) => {
            childAgent = child;
            if (wantsSpawn) {
                childSteerSubscriber = instrumentSteer(child);
                // Stash the registry on the child Agent so any UI
                // plumbing that wants to observe live grandchild state
                // can pick it up. Mirrors the parent-Agent pattern in
                // piChat.js.
                try { child._childHandleRegistry = childRegistry; } catch { /* noop */ }
            }
        },
        childRegistry,
    };
}

// Race a child's completion against a parent-steer event. Resolves
// with { kind: "done", result } when the child finishes (normally or
// in error), or { kind: "interrupted" } when the user steers the
// parent agent first. Used by spawn_subagent / spawn_batch so a user
// steer mid-spawn can return early with handles instead of waiting
// for the whole delegation to finish.
function raceCompletionVsSteer(childPromise, subscribeToParentSteer) {
    return new Promise((resolve) => {
        let resolved = false;
        let unsubscribeSteer = null;
        const finish = (r) => {
            if (resolved) return;
            resolved = true;
            if (unsubscribeSteer) {
                try { unsubscribeSteer(); } catch { /* noop */ }
            }
            resolve(r);
        };
        unsubscribeSteer = subscribeToParentSteer(() => {
            finish({ kind: "interrupted" });
        });
        childPromise.then(
            (result) => finish({ kind: "done", result }),
            (err) => finish({ kind: "done", result: { status: "error", error: err, result: null, resultRef: null, messages: [] } }),
        );
    });
}

// Create the spawn_subagent tool. `toolRegistry` is the array of
// AgentTools the chat itself has; the spawned child gets a subset by
// name. `scrubbedToolNames` is the caller-provided block list for
// tools that should never be exposed to children regardless of what
// the LLM requests.
//
// `subscribeToParentSteer` (optional) is a function that registers a
// callback fired when the user steers the parent agent. When provided
// alongside `childRegistry`, the tool races child completion against
// parent steer: on steer-during-spawn, the tool returns early with a
// handle id, the child keeps running in the background, and the
// supervisor tools (list/steer/abort/await_subagent) take over from
// there. When omitted, the tool falls back to today's
// await-end-to-end behavior — this keeps grandchild spawns (where we
// don't thread the supervisor wiring) working unchanged.
//
// Note: spawn_subagent and spawn_batch are NOT blocked here — a child
// is allowed to spawn grand-children. The chain remains stoppable
// because each spawn level wires its outer abort signal through
// makeChildController; stopping a parent cascades downward.
export function createSpawnSubAgentTool({ toolRegistry, scrubbedToolNames = [], getNamespaceKey, subscribeToParentSteer, childRegistry, rehydrate, getThinkingLevel, serverChatId = null, getGraph = null } = {}) {
    const blocked = new Set(scrubbedToolNames);
    const availableNames = () =>
        toolRegistry()
            .map((t) => t.name)
            .filter((n) => !blocked.has(n));

    return {
        name: "spawn_subagent",
        label: "Spawn sub-agent",
        description:
            "Delegate a focused task to a fresh sub-agent with its own context. The child reuses your LLM credentials and (only) the tools you list. Use when:\n" +
            "  • the task is self-contained and doesn't need the rest of this chat's history,\n" +
            "  • you want clean context (e.g. evaluating something against a fixed rule),\n" +
            "  • the task would otherwise burn many of your own turns.\n" +
            "Children may themselves call spawn_subagent / spawn_batch to recurse — useful for decomposing a problem then re-aggregating. A global cap limits total sub-agents in flight; stop signals cascade from parent to all descendants.\n" +
            "Returns { status, result }. If `output_schema` is provided, the child must call `return_result` with a matching object and that captured value is returned; otherwise the child's last assistant message is returned.\n\n" +
            "Row-ref handoff (child → parent): if the child fetched row-shaped data (e.g. analytics_query) and its answer IS those rows, it can call return_result with `result_ref` instead of inlining. When that happens, the result text ends with `--- _tool_call_id ---` followed by an id you can pass into execute_python via `from_tool_calls` — no need to re-emit rows.\n\n" +
            "Row-ref handoff (parent → child): pass `from_tool_calls` to stage a slice of a row-shaped result you already fetched into the child's initial context. Shape: `{ <var>: { tool_call_id, slice?: [start, end] } }`. Each entry appears in the child's history as a synthetic prior analytics_query call, with the sliced rows already in the result — the child can read them, page them via `expand_preview`, or pipe them into its own `execute_python` via `from_tool_calls`, all without you re-emitting rows in args. Use slicing to fan out work over a large dataset: spawn N children, each with rows[i*K:(i+1)*K]. The child's mapping from your var names to the synthetic tool_call_ids is appended to its task text.",
        parameters: Type.Object({
            task: Type.String({
                description:
                    "The complete user-message for the child. Include all context the child needs — it can't see this chat's history. Be specific about what shape of answer you want.",
            }),
            system_prompt: Type.Optional(
                Type.String({
                    description:
                        "Optional system prompt for the child (its persona / rules / domain knowledge). If omitted, the child runs without a system prompt.",
                }),
            ),
            tools: Type.Optional(
                Type.Array(Type.String(), {
                    description:
                        "Names of tools the child may call (subset of your own toolkit). OMIT THIS FIELD to give the child the default investigation toolkit: schema_search, schema_describe_entity, schema_list_entities, analytics_query — covers most tasks. Pass an explicit `[]` only when you want a pure-reasoning child with no tools. Listing a specific subset is fine when you want to constrain (e.g. schema-only).",
                }),
            ),
            output_schema: Type.Optional(
                Type.Unknown({
                    description:
                        "Optional JSON-schema-ish object describing the expected return shape. When provided, the child gets a `return_result(result)` tool it MUST call once with a matching value. Without this, you get the child's last assistant text as a string.",
                }),
            ),
            anchor: Type.Optional(
                Type.String({
                    description:
                        "A node key from graph_state (e.g. `session:<id>`, `endpoint:<id>`). The child starts from that anchor's neighbourhood in the investigation graph and writes what it establishes back with `claim`; use this to fan an investigation out across sessions or endpoints without re-explaining what is already known.",
                }),
            ),
            from_tool_calls: Type.Optional(
                Type.Record(Type.String(), Type.Unknown(), {
                    description:
                        "Optional map of staged inputs for the child. Shape: `{ <var>: { tool_call_id, slice?: [start, end] } }`. Each entry stages a slice of a prior row-shaped tool result (analytics_query / execute_python) into the child's initial context as a synthetic prior tool call. Omit `slice` to hand over the full result. Use for fan-out: spawn children with different slices of the same dataset.",
                }),
            ),
        }),
        async execute(toolCallId, args, signal, onUpdate) {
            const resolvedTools = resolveTools(toolRegistry(), args.tools, blocked);
            // Child-scoped spawn / supervisor tools so this child's
            // grandchildren never share a steer-listener set with the
            // parent. A user steer of THIS parent only fires THIS
            // parent's listeners; the child's grandchildren stay
            // unaware until the parent's LLM explicitly steers the
            // child via steer_subagent.
            const supervision = buildChildSupervision(resolvedTools, { getNamespaceKey, scrubbedToolNames });
            const childTools = supervision.tools;
            const { childSignal, abort } = makeChildController(signal);
            const namespaceKey = getNamespaceKey?.() || "default";
            const staged = await buildStagedInputs({
                refs: args.from_tool_calls,
                namespaceKey,
                spawnToolCallId: toolCallId,
                rehydrate,
                signal,
            });
            if (staged.error) {
                return {
                    content: [{ type: "text", text: `error: ${staged.error}` }],
                    isError: true,
                };
            }
            const childSystemPrompt = await buildChildSystemPrompt(
                args.system_prompt,
                childTools,
                args.output_schema != null,
            );
            const brief = args.anchor ? anchorBrief(getGraph?.(), args.anchor) : null;
            const childTask = appendStagedInputsFooter(brief ? `${brief}\n\n${args.task}` : args.task, staged.ids);
            const state = {
                kind: "subagent",
                taskSummary: summarise(args.task),
                status: "running",
                activity: "starting…",
                tools: [],
                preview: "",
                result: null,
                summary: null,
                // UI hook: while running, the rendered tool node can
                // call this to stop the child. Cleared once resolved.
                abort,
                // Observability: full transcript handles. `messages`
                // mirrors the child's conversation (see
                // mirrorTranscript), re-read on every child event.
                task: childTask,
                systemPrompt: childSystemPrompt,
                messages: [],
                stagedInputs: Object.entries(staged.ids).map(([varName, stagedId]) => ({
                    varName,
                    stagedId,
                })),
            };
            const emit = makeThrottledEmit(onUpdate, () => state);

            // Assigned at interrupt time (see below). When set, onEvent
            // fires `notifyStateChange` so the chat UI keeps reflecting
            // the running child's state after this tool returns.
            let registeredHandleId = null;
            const onEvent = (event) => {
                forwardChildEvent(state, event);
                emit();
                if (registeredHandleId && childRegistry) {
                    childRegistry.notifyStateChange(registeredHandleId);
                }
            };
            const onChildReady = (child) => {
                supervision.instrument(child);
                mirrorTranscript(state, child);
            };

            const handle = await startUnderInFlightCap({
                task: childTask,
                systemPrompt: childSystemPrompt,
                tools: childTools,
                outputSchema: args.output_schema,
                signal: childSignal,
                onEvent,
                onChildReady,
                initialMessages: staged.messages,
                // Inherit the parent chat's current thinking depth so a
                // delegated child reasons at the same level the user picked.
                thinkingLevel: getThinkingLevel?.(),
                // Attribute the child's LLM proxy calls to the spawning chat.
                serverChatId,
            });

            // If supervisor wiring is available and we actually built a
            // child, race its completion against a parent-steer event.
            // Otherwise await end-to-end (today's behavior for
            // grandchildren and the no-supervisor fallback).
            let raceResult;
            if (handle.child && subscribeToParentSteer && childRegistry) {
                raceResult = await raceCompletionVsSteer(handle.promise, subscribeToParentSteer);
            } else {
                raceResult = { kind: "done", result: await handle.promise };
            }

            if (raceResult.kind === "interrupted") {
                // User steered the parent mid-spawn. Register the
                // still-running child against the chat's child registry
                // so the supervisor tools can find it, surrender
                // control to the parent loop with a snapshot result.
                const handleId = `sub_${toolCallId}`;
                state.handleId = handleId;
                state.activity = "running in background";
                // Wire onEvent's notifyStateChange path. From this
                // point on, every child event also wakes the
                // registry's listeners, so the chat UI keeps the tool
                // node's details in sync with the live state.
                registeredHandleId = handleId;
                childRegistry.add(handleId, {
                    kind: "subagent",
                    toolCallId,
                    child: handle.child,
                    promise: handle.promise,
                    state,
                    taskSummary: state.taskSummary,
                    abort: handle.abort,
                });
                // Once the child naturally finishes, surface the
                // final status onto state so the chat UI shows the
                // correct terminal state (done / stopped / error)
                // even if the supervisor never calls await_subagent.
                // We deliberately do NOT auto-remove the handle from
                // the registry on completion — keep it around so the
                // LLM can still call await_subagent later to collect
                // the result. Explicit abort_subagent / await_subagent
                // are responsible for removal.
                handle.promise.then((result) => {
                    if (state.status !== "done" && state.status !== "stopped" && state.status !== "error") {
                        state.status = result.status === "done"
                            ? "done"
                            : result.status === "aborted" ? "stopped" : "error";
                    }
                    state.activity = null;
                    if (result.status === "done") {
                        state.result = result.result;
                        if (!state.summary) {
                            state.summary = summariseResult(result.result, args.output_schema);
                        }
                    } else if (result.status === "error") {
                        state.summary = result.error?.message || "sub-agent failed";
                    } else if (result.status === "aborted") {
                        state.summary = "stopped by user";
                    }
                    childRegistry.notifyStateChange(handleId);
                }, () => { /* error already surfaced via state.status */ });
                emit.flush();
                return {
                    content: [{ type: "text", text:
                        `Spawn interrupted by user steer (the user sent a message while this child was still running). ` +
                        `The child is now running in the background with handle id: ${handleId}.\n\n` +
                        `You'll see the user's new message in this turn. Options for managing this child:\n` +
                        `  • steer_subagent({ child_id: "${handleId}", message }) — inject new instructions; the child adapts at its next turn boundary\n` +
                        `  • abort_subagent({ child_id: "${handleId}" }) — stop the child if its work is no longer relevant\n` +
                        `  • await_subagent({ child_id: "${handleId}" }) — block until the child finishes and return its result\n` +
                        `  • list_subagents() — see every live handle if there are others\n\n` +
                        `If the user's new direction doesn't affect this child, you may ignore the handle and respond normally; the child will finish on its own and be cleaned up.`
                    }],
                    details: { ...state, abort: null },
                };
            }

            const { status, result, resultRef, error } = raceResult.result;

            const stopped = status === "aborted";
            state.status = status === "done" ? "done" : stopped ? "stopped" : "error";
            state.activity = null;
            state.result = result;
            state.summary =
                status === "done"
                    ? summariseResult(result, args.output_schema)
                    : stopped
                        ? "stopped by user"
                        : error?.message || "sub-agent failed";
            state.abort = null;

            const promoted = status === "done"
                ? promoteResultRef({
                    resultRef,
                    toolCallId,
                    namespaceKey: getNamespaceKey?.() || "default",
                    source: "spawn_subagent",
                })
                : null;
            if (promoted) state.promotedRows = promoted;
            emit.flush();

            let textPayload;
            if (status === "done") {
                if (result != null) {
                    textPayload = typeof result === "string" ? result : JSON.stringify(result);
                } else if (promoted) {
                    textPayload = `sub-agent surfaced ${promoted.rowCount} rows from a prior ${promoted.originalSource} call.`;
                } else {
                    textPayload = "(no result)";
                }
                if (promoted) {
                    textPayload += `\n--- _tool_call_id ---\n${toolCallId}`;
                }
            } else if (stopped) {
                textPayload = "sub-agent stopped by user";
            } else {
                textPayload = `error: ${error?.message || "sub-agent failed"}`;
            }

            return {
                content: [{ type: "text", text: textPayload }],
                details: { ...state, abort: null },
                isError: status !== "done" && !stopped,
            };
        },
    };
}

// spawn_batch: same task across many items, concurrency-limited.
// Each item is rendered into the task via {{item}} substitution; the
// model can also reference {{item.field}} if it passed objects.
// Children may themselves call spawn_subagent / spawn_batch — the
// MAX_AGENTS_IN_FLIGHT cap and the abort cascade keep recursion safe.
//
// Like spawn_subagent, the batch supports parent-steer interruption
// when `subscribeToParentSteer` and `childRegistry` are wired in: a
// steer mid-batch surfaces a single batch handle to the parent. The
// supervisor's steer_subagent broadcasts to all live items in that
// batch; abort_subagent stops them all; await_subagent waits for
// every item to settle. Per-item supervision isn't supported — if
// you need that granularity, spawn the items individually.
export function createSpawnBatchTool({ toolRegistry, scrubbedToolNames = [], getNamespaceKey, subscribeToParentSteer, childRegistry, rehydrate, getThinkingLevel, serverChatId = null, getGraph = null } = {}) {
    const blocked = new Set(scrubbedToolNames);

    return {
        name: "spawn_batch",
        label: "Spawn batch of sub-agents",
        description:
            "Fan out the same task across many items, one sub-agent per item, with bounded concurrency. Use for parallel evaluations — e.g. running a rule against a list of sessions, summarising a list of issues, etc. When the items are anchors the investigation graph already holds (sessions, endpoints, processes from graph_state), `expand_all` does the standard investigation of each without a sub-agent writing queries; reach for spawn_batch when the per-item task is something expand does not cover.\n\n" +
            "The `task_template` string supports `{{item}}` substitution: if items are strings, it's the string itself; if items are objects, `{{item.field}}` reads a field. Children share `system_prompt` and tool allowlist. If `output_schema` is set, each child must call `return_result` and the returned object is captured per-item.\n\n" +
            "Returns a summary of per-item statuses. Per-item results are visible in the streaming UI and structured in the tool result's `details.items`.\n\n" +
            "Row-ref handoff (child → parent): if any child calls return_result with `result_ref` (an id of row-shaped data it fetched), the result text lists the per-item tool_call_ids under `--- _tool_call_ids ---`. Pass any of those into execute_python's `from_tool_calls` to pipe the rows in without re-emission.\n\n" +
            "Row-ref handoff (parent → child, PER ITEM): each `items[i]` may be an object with an optional `from_tool_calls` field — `{ <var>: { tool_call_id, slice?: [start, end] } }`. The slice is staged into that child's initial context as a synthetic prior analytics_query. This is the fan-out shape: pass `items: [{ id: 'chunk0', from_tool_calls: { rows: { tool_call_id: 'toolu_…', slice: [0, 400] } } }, { id: 'chunk1', ..., slice: [400, 800] }, …]` to run the same task against successive slices of a large dataset.",
        parameters: Type.Object({
            items_are_anchors: Type.Optional(
                Type.Boolean({
                    description:
                        "When true, each item is a node key from graph_state (e.g. `session:<id>`) and that child starts from the anchor's neighbourhood in the investigation graph, writing back with `claim`. `{{item}}` still substitutes the key into the template.",
                }),
            ),
            task_template: Type.String({
                description:
                    "Task description with `{{item}}` (or `{{item.field}}`) placeholders. Each item replaces these before being sent to its child.",
            }),
            items: Type.Array(Type.Unknown(), {
                description:
                    "List of items to fan out over. Strings or simple objects work best. Capped at 50 to limit cost.",
            }),
            system_prompt: Type.Optional(Type.String()),
            tools: Type.Optional(
                Type.Array(Type.String(), {
                    description:
                        "Names of tools each child may call. Omit to use the default investigation toolkit (schema_search, schema_describe_entity, schema_list_entities, analytics_query). Pass `[]` for pure-reasoning children with no tools.",
                }),
            ),
            output_schema: Type.Optional(Type.Unknown()),
            concurrency: Type.Optional(
                Type.Number({ description: `Children in flight at once (default ${DEFAULT_BATCH_CONCURRENCY}).` }),
            ),
        }),
        async execute(toolCallId, args, signal, onUpdate) {
            const items = Array.isArray(args.items) ? args.items.slice(0, MAX_BATCH_ITEMS) : [];
            // Resolved once; each item wraps it for its own
            // supervision scope. The resolved array itself is shared
            // by reference (cheap), but every item builds its own
            // childHandleRegistry + steer-listener set + scoped
            // spawn/supervisor tools.
            const resolvedTools = resolveTools(toolRegistry(), args.tools, blocked);
            const state = {
                kind: "batch",
                taskSummary: summarise(args.task_template),
                total: items.length,
                done: 0,
                errored: 0,
                items: items.map((raw, i) => ({
                    id: itemId(raw, i),
                    label: itemLabel(raw, i),
                    status: "queued",
                    activity: null,
                    tools: [],
                    preview: "",
                    result: null,
                    summary: null,
                })),
            };
            const emit = makeThrottledEmit(onUpdate, () => state);
            emit();

            if (!items.length) {
                emit.flush();
                return {
                    content: [{ type: "text", text: "no items provided" }],
                    details: { ...state },
                };
            }

            // Resolve the system prompt ONCE so every child in this
            // batch sees the exact same string. Prefix caching on
            // Anthropic (and similar) means the analytics-guide
            // portion is paid for once, not once-per-child.
            const sharedSystemPrompt = await buildChildSystemPrompt(
                args.system_prompt,
                resolvedTools,
                args.output_schema != null,
            );

            const namespaceKey = getNamespaceKey?.() || "default";

            // Per-item handle registry so the batch can broadcast
            // steer / abort across all live items when the parent
            // surfaces a batch handle after interruption.
            const itemHandles = new Map();
            // Sticky flag the per-item task functions check at the
            // top so tasks that haven't started yet bail when the
            // batch is interrupted or aborted via the supervisor.
            let batchInterrupted = false;
            // Set at interrupt time. Once assigned, every per-item
            // onEvent fires notifyStateChange so the chat UI keeps
            // reflecting live batch state after this tool returns.
            let registeredBatchHandleId = null;

            const tasks = items.map((rawItem, idx) => async () => {
                if (signal?.aborted || batchInterrupted) return;
                // Per-item abort: parent abort still cascades, but
                // the UI can also target one child without killing
                // its siblings. The abort fn rides on the item state
                // so the rendered row can wire it to a Stop button.
                const { childSignal, abort } = makeChildController(signal);
                const filled = fillTemplate(args.task_template, rawItem);
                // Per-item staging: items can carry their own
                // from_tool_calls so each child gets a different
                // slice of the parent's row data.
                const itemRefs = (rawItem && typeof rawItem === "object" && rawItem.from_tool_calls && typeof rawItem.from_tool_calls === "object")
                    ? rawItem.from_tool_calls
                    : null;
                const itemSpawnId = `${toolCallId}:${idx}`;
                const itemStaged = await buildStagedInputs({
                    refs: itemRefs,
                    namespaceKey,
                    spawnToolCallId: itemSpawnId,
                    rehydrate,
                    signal: childSignal,
                });
                if (itemStaged.error) {
                    state.items[idx] = {
                        ...state.items[idx],
                        status: "error",
                        activity: null,
                        summary: itemStaged.error,
                        abort: null,
                    };
                    state.errored += 1;
                    emit();
                    return;
                }
                const itemBrief = args.items_are_anchors && typeof rawItem === "string" ? anchorBrief(getGraph?.(), rawItem) : null;
                const itemTask = appendStagedInputsFooter(itemBrief ? `${itemBrief}\n\n${filled}` : filled, itemStaged.ids);
                state.items[idx] = {
                    ...state.items[idx],
                    status: "running",
                    activity: "starting…",
                    abort,
                    task: itemTask,
                    systemPrompt: sharedSystemPrompt,
                    messages: [],
                    stagedInputs: Object.entries(itemStaged.ids).map(([varName, stagedId]) => ({
                        varName,
                        stagedId,
                    })),
                };
                emit();

                const onEvent = (event) => {
                    forwardChildEvent(state.items[idx], event);
                    emit();
                    if (registeredBatchHandleId && childRegistry) {
                        childRegistry.notifyStateChange(registeredBatchHandleId);
                    }
                };
                // Each item gets its own supervision scope — its own
                // grandchild registry and steer-listener set — so a
                // user steer of the PARENT chat doesn't propagate down
                // into this item's grandchildren.
                const itemSupervision = buildChildSupervision(resolvedTools, { getNamespaceKey, scrubbedToolNames });
                const itemTools = itemSupervision.tools;
                const onChildReady = (child) => {
                    itemSupervision.instrument(child);
                    state.items[idx] = { ...state.items[idx] };
                    mirrorTranscript(state.items[idx], child);
                };

                const itemHandle = await startUnderInFlightCap({
                    task: itemTask,
                    systemPrompt: sharedSystemPrompt,
                    tools: itemTools,
                    outputSchema: args.output_schema,
                    signal: childSignal,
                    onEvent,
                    onChildReady,
                    initialMessages: itemStaged.messages,
                    // Each batch item inherits the parent chat's thinking depth.
                    thinkingLevel: getThinkingLevel?.(),
                    // Attribute the child's LLM proxy calls to the spawning chat.
                    serverChatId,
                });
                if (itemHandle.child) {
                    itemHandles.set(idx, itemHandle);
                }
                const { status, result, resultRef, error } = await itemHandle.promise;
                itemHandles.delete(idx);
                const ok = status === "done";
                const stopped = status === "aborted";

                // Synthesize a per-item handle off the batch's own
                // tool_call_id so each child's row-ref result is
                // separately addressable from `from_tool_calls`.
                const itemToolCallId = `${toolCallId}:${idx}`;
                const promoted = ok
                    ? promoteResultRef({
                        resultRef,
                        toolCallId: itemToolCallId,
                        namespaceKey: getNamespaceKey?.() || "default",
                        source: "spawn_batch",
                    })
                    : null;

                state.items[idx] = {
                    ...state.items[idx],
                    status: ok ? "done" : stopped ? "stopped" : "error",
                    activity: null,
                    result: ok ? result : null,
                    resultRefToolCallId: promoted ? itemToolCallId : null,
                    promotedRows: promoted,
                    summary: ok
                        ? summariseResult(result, args.output_schema)
                        : stopped
                            ? "stopped by user"
                            : error?.message || "sub-agent failed",
                    abort: null,
                };
                if (ok) state.done += 1;
                else if (!stopped) state.errored += 1;
                emit();
            });

            const batchPromise = runWithConcurrency(tasks, args.concurrency || DEFAULT_BATCH_CONCURRENCY);

            // Race the whole batch's completion against a parent
            // steer when the supervisor wiring is available. On
            // interrupt, the batch surfaces as a single handle —
            // steer broadcasts to every live item; abort kills them
            // all; await waits for everything to settle.
            let raceResult;
            if (subscribeToParentSteer && childRegistry) {
                raceResult = await raceCompletionVsSteer(batchPromise, subscribeToParentSteer);
            } else {
                raceResult = { kind: "done", result: await batchPromise };
            }

            if (raceResult.kind === "interrupted") {
                batchInterrupted = true;
                const handleId = `batch_${toolCallId}`;
                state.handleId = handleId;
                // Activate live UI sync — every subsequent per-item
                // event now also wakes the registry's listeners.
                registeredBatchHandleId = handleId;
                const liveCount = itemHandles.size;
                const notStartedCount = state.items.filter((i) => i.status === "queued").length;

                // Wrap the still-running batchPromise so the handle
                // resolves with the final aggregated state once all
                // (now-finishing) items settle.
                const batchHandlePromise = (async () => {
                    try { await batchPromise; } catch { /* per-item errors already in state */ }
                    // Final notify so the chat UI picks up the
                    // settled per-item counts (done/errored/stopped)
                    // before the supervisor potentially awaits.
                    childRegistry.notifyStateChange(handleId);
                    return state;
                })();

                childRegistry.add(handleId, {
                    kind: "batch",
                    toolCallId,
                    child: null,
                    promise: batchHandlePromise,
                    state,
                    taskSummary: state.taskSummary,
                    steer: (msg) => {
                        for (const h of itemHandles.values()) {
                            try { h.child?.steer(msg); } catch { /* noop */ }
                        }
                    },
                    abort: () => {
                        batchInterrupted = true;
                        for (const h of itemHandles.values()) {
                            try { h.abort(); } catch { /* noop */ }
                        }
                    },
                });
                // Deliberately no auto-remove on natural completion —
                // the supervisor's await_subagent / abort_subagent
                // are responsible for removal so the LLM can still
                // collect the batch's result after the children
                // naturally finish.

                emit.flush();
                return {
                    content: [{ type: "text", text:
                        `Batch interrupted by user steer. ${liveCount} child${liveCount === 1 ? "" : "ren"} still running in the background under batch handle: ${handleId} ` +
                        `(${state.done} of ${state.total} done, ${state.errored} errored, ${notStartedCount} not yet started — items not yet started will not run).\n\n` +
                        `You'll see the user's new message in this turn. Manage the batch with:\n` +
                        `  • steer_subagent({ child_id: "${handleId}", message }) — broadcasts the steer to all live items\n` +
                        `  • abort_subagent({ child_id: "${handleId}" }) — stops every live item in this batch\n` +
                        `  • await_subagent({ child_id: "${handleId}" }) — blocks until every item has settled\n` +
                        `  • list_subagents() — see every live handle\n\n` +
                        `Per-item supervision isn't available on a batch handle; if you need that, spawn the items individually.`
                    }],
                    details: { ...state, items: state.items.map((i) => ({ ...i, abort: null })) },
                };
            }

            emit.flush();
            const stoppedCount = state.items.filter((i) => i.status === "stopped").length;
            const summary = [
                `${state.done}/${state.total} succeeded`,
                state.errored ? `${state.errored} errored` : null,
                stoppedCount ? `${stoppedCount} stopped` : null,
            ].filter(Boolean).join(", ");
            // Strip abort fns from the final details payload — they're
            // closure references that don't belong in the persistent
            // tool result.
            const finalItems = state.items.map((i) => ({ ...i, abort: null }));

            // If any items surfaced row-shaped results via result_ref,
            // list the per-item tool_call_ids so the parent can pipe
            // them into execute_python via `from_tool_calls`.
            const promotedLines = finalItems
                .map((i, idx) => (i.resultRefToolCallId
                    ? `  • ${i.resultRefToolCallId}  (item ${idx}: ${i.label}, ${i.promotedRows?.rowCount ?? "?"} rows)`
                    : null))
                .filter(Boolean);
            const textPayload = promotedLines.length
                ? `${summary}\n\n--- _tool_call_ids ---\nThe following items surfaced row-shaped results that can be referenced via from_tool_calls:\n${promotedLines.join("\n")}`
                : summary;

            return {
                content: [{ type: "text", text: textPayload }],
                details: { ...state, items: finalItems },
            };
        },
    };
}

// ── helpers ───────────────────────────────────────────────────────

// Make a per-child AbortController whose signal merges the outer
// (parent) signal with a local abort. The returned `abort` function
// is what the UI calls on the Stop button; `childSignal` is what we
// pass into runSubAgent so the child propagates either trigger.
function makeChildController(outerSignal) {
    const controller = new AbortController();
    if (outerSignal) {
        if (outerSignal.aborted) {
            controller.abort();
        } else {
            outerSignal.addEventListener("abort", () => controller.abort(), { once: true });
        }
    }
    return {
        childSignal: controller.signal,
        abort: () => {
            try { controller.abort(); } catch { /* noop */ }
        },
    };
}

// Start a sub-agent under the in-flight cap. Returns a handle
// { child, abort, promise } whose promise resolves with the same
// shape runSubAgent returns. The counter is incremented when a
// child is successfully constructed and decremented when its promise
// settles — works for both fully-awaited completions and handles
// that survive past their spawn tool's return.
//
// On cap rejection or runtime failure, returns a handle with
// `child: null` and a pre-resolved error promise so callers can
// uniformly switch on `handle.child` to decide whether to set up
// mid-run controls.
async function startUnderInFlightCap(opts) {
    if (_agentsInFlight >= MAX_AGENTS_IN_FLIGHT) {
        return {
            child: null,
            abort: () => {},
            promise: Promise.resolve({
                status: "error",
                error: new Error(
                    `sub-agent in-flight cap reached (${MAX_AGENTS_IN_FLIGHT}). A parent likely fanned out too widely or recursion went too deep. Wait for some children to finish, or stop the batch and re-issue with a smaller fan-out.`,
                ),
                result: null,
                messages: [],
                turns: 0,
            }),
        };
    }
    const handle = await startSubAgent(opts);
    if (handle.child) {
        _agentsInFlight += 1;
        const inner = handle.promise;
        handle.promise = inner.finally(() => {
            _agentsInFlight = Math.max(0, _agentsInFlight - 1);
        });
    }
    return handle;
}

// Convenience wrapper for callers that want today's behavior: start
// and await the child end-to-end.
async function runUnderInFlightCap(opts) {
    const handle = await startUnderInFlightCap(opts);
    return handle.promise;
}

// Compose the child's effective system prompt: schema-derived analytics
// guidance FIRST (so identical across children in a batch → prefix
// cache hits) with today's date after it (day-granular, so a batch still
// shares it), then caller-supplied persona, then the return-result
// reminder. The schema examples block is the spec contract the child
// needs to compose valid analytics_query calls.
async function buildChildSystemPrompt(userSystemPrompt, childTools, hasOutputSchema) {
    const parts = [];
    if (childTools.some((t) => t.name === "analytics_query")) {
        parts.push(await buildAnalyticsGuide());
        parts.push(currentDateSection());
    }
    if (userSystemPrompt && userSystemPrompt.trim()) parts.push(userSystemPrompt.trim());
    if (hasOutputSchema) parts.push(RETURN_RESULT_REMINDER);
    return parts.join("\n\n");
}

// `requestedNames`:
//   undefined / null → fall back to DEFAULT_CHILD_TOOLKIT
//   []               → respected as "no tools" (caller opt-out)
//   [names...]       → exactly those, filtered against the registry
// Anything in `blocked` (spawn_* etc.) is silently dropped regardless.
function resolveTools(registry, requestedNames, blocked) {
    const names = requestedNames == null ? DEFAULT_CHILD_TOOLKIT : requestedNames;
    if (!names.length) return [];
    const byName = new Map(registry.map((t) => [t.name, t]));
    const out = [];
    for (const name of names) {
        if (blocked.has(name)) continue;
        const t = byName.get(name);
        if (t) out.push(t);
    }
    return out;
}

function summarise(text) {
    if (!text) return "";
    const clean = String(text).replace(/\s+/g, " ").trim();
    return clean.length > 80 ? `${clean.slice(0, 77)}…` : clean;
}

// ── inspect_subagent ──────────────────────────────────────────────
//
// Lookup tool for read-only inspection of a prior spawn_subagent /
// spawn_batch call's internal transcript. The kernel-extracting agent
// uses this to flatten a sub-agent's analytics_query / execute_python
// / render_block calls into the kernel's `steps` when the recipe
// depends on data the child produced.
//
// `chat` is the parent chat handle (same one `summariseRecentWindow`
// reads). The tool walks `chat.items` to find the spawn_* tool by id,
// then summarises the child's messages array (single child) or each
// item's messages array (batch).
export function createInspectSubAgentTool({ chat } = {}) {
    return {
        name: "inspect_subagent",
        label: "Inspect sub-agent transcript",
        description:
            "Look up a prior spawn_subagent or spawn_batch tool call in this chat by its tool_call_id and return a structured summary of the child's (or each child's) internal tool calls — analytics_query specs, execute_python code, render_block specs, return_result outcomes. " +
            "Use this when building a kernel where a spawn_* invocation produced load-bearing data and the kernel needs to reproduce the child's recipe — you'll lift the child's successful analytics_query / execute_python calls into the kernel's `steps` as if they had happened in the main chat. " +
            "Returns ONLY a structured text summary; it does not re-execute anything.",
        parameters: Type.Object({
            tool_call_id: Type.String({
                description: "The tool_call_id of the spawn_subagent or spawn_batch entry from the transcript (e.g. 'toolu_…'). Listed alongside the tool name as `[id=…]` in the transcript summary you were given.",
            }),
        }),
        async execute(_id, args) {
            const id = String(args?.tool_call_id || "").trim();
            if (!id) {
                return errorContent("inspect_subagent: tool_call_id is required.");
            }
            const items = Array.isArray(chat?.items) ? chat.items : [];
            const target = findToolByCallId(items, id);
            if (!target) {
                return errorContent(
                    `inspect_subagent: no tool call with id ${id} found in this chat. Use the [id=…] tag shown next to spawn_* entries in the transcript summary.`,
                );
            }
            if (target.name !== "spawn_subagent" && target.name !== "spawn_batch") {
                return errorContent(
                    `inspect_subagent: ${id} is a ${target.name} call, not a sub-agent invocation. Only spawn_subagent / spawn_batch transcripts are inspectable.`,
                );
            }
            const text = target.name === "spawn_batch"
                ? summariseBatchTranscript(target)
                : summariseChildTranscript(target);
            return { content: [{ type: "text", text }] };
        },
    };
}

function errorContent(message) {
    return {
        content: [{ type: "text", text: message }],
        isError: true,
    };
}

function findToolByCallId(items, id) {
    for (const item of items) {
        if (item?.kind === "tool" && item.id === id) return item;
        const children = Array.isArray(item?.children) ? item.children : [];
        for (const c of children) {
            if (c?.kind === "tool" && c.id === id) return c;
        }
    }
    return null;
}

function summariseChildTranscript(toolItem) {
    const details = toolItem?.details || {};
    const messages = Array.isArray(details.messages) ? details.messages : [];
    const head = [
        `# Sub-agent transcript (${toolItem.name}, id=${toolItem.id})`,
        details.taskSummary ? `Task: ${details.taskSummary}` : null,
        details.status ? `Status: ${details.status}` : null,
        details.summary ? `Outcome: ${details.summary}` : null,
        "",
    ].filter((x) => x !== null);
    const body = summariseAgentMessages(messages);
    return [...head, body || "(no tool calls in child transcript)"].join("\n");
}

function summariseBatchTranscript(toolItem) {
    const details = toolItem?.details || {};
    const items = Array.isArray(details.items) ? details.items : [];
    const head = [
        `# Batch sub-agent transcript (${toolItem.name}, id=${toolItem.id})`,
        details.taskSummary ? `Task template: ${details.taskSummary}` : null,
        `Items: ${items.length}, done=${details.done || 0}, errored=${details.errored || 0}`,
        "",
    ].filter((x) => x !== null);
    const blocks = items.map((item, idx) => {
        const messages = Array.isArray(item?.messages) ? item.messages : [];
        const subhead = `## Item ${idx} (${item?.label || idx}) — status=${item?.status || "?"}`;
        const body = summariseAgentMessages(messages) || "(no tool calls)";
        return [subhead, body, ""].join("\n");
    });
    return [...head, ...blocks].join("\n");
}

// Walk a pi-agent messages array and produce a compact summary of the
// assistant's tool calls paired with their results. Mirrors the shape
// of `summariseTool` in kernelSaveAgent.js but operates on raw pi-agent
// messages instead of chat-item tool nodes — the chat doesn't keep
// per-call rich `details` for the child's calls, only the messages.
function summariseAgentMessages(messages) {
    if (!messages.length) return "";
    const resultsById = new Map();
    for (const m of messages) {
        // pi-ai represents a tool result as a top-level message
        // ({ role:"toolResult", toolCallId, content }); the previous
        // content-block scan never matched that shape, so every child
        // call rendered "(no result captured)". Index the top-level
        // shape (and keep the content-block scan as a defensive fallback).
        if (m?.role === "toolResult" && m.toolCallId) {
            resultsById.set(m.toolCallId, m);
        }
        const content = Array.isArray(m?.content) ? m.content : [];
        for (const c of content) {
            if (c?.type === "toolResult" && c.toolCallId) {
                resultsById.set(c.toolCallId, c);
            }
        }
    }
    const lines = [];
    let turn = 0;
    for (const m of messages) {
        if (m?.role !== "assistant") continue;
        const content = Array.isArray(m.content) ? m.content : [];
        const calls = content.filter((c) => c?.type === "toolCall");
        if (!calls.length) continue;
        turn += 1;
        lines.push(`### Child turn ${turn}`);
        for (const call of calls) {
            const result = resultsById.get(call.id);
            lines.push(summariseChildCall(call, result));
        }
        lines.push("");
    }
    return lines.join("\n").trim();
}

function summariseChildCall(call, result) {
    const name = call?.name || "tool";
    // pi-ai stores a tool call's args under `arguments` in state.messages
    // (anthropic.js builds `{ type:"toolCall", arguments: … }`); staged
    // hand-off blocks carry `input`. Read `arguments` first, fall back to
    // `input`. Reading only `input` here was why inspect_subagent rendered
    // every child analytics_query / execute_python / render_block as
    // "(no args)" — blanking the very specs the kernel extractor needs.
    const callArgs = call?.arguments ?? call?.input;
    const argText = formatChildArgs(callArgs);
    const isError = result?.isError === true;
    const status = result ? (isError ? "error" : "done") : "no-result";
    const resultText = result ? extractResultText(result) : "(no result captured)";
    if (name === "analytics_query") {
        return [`- analytics_query (${status})`, indent(formatChildSpec(callArgs), "    spec: "), `    → ${truncate(resultText, 200)}`].join("\n");
    }
    if (name === "execute_python") {
        const code = typeof callArgs === "object" ? callArgs?.code : null;
        // Full code — it's a kernel step the runner executes verbatim.
        const codePreview = typeof code === "string"
            ? "    code:\n" + code.split("\n").map((l) => "      " + l).join("\n")
            : indent(argText, "    args: ");
        return [`- execute_python (${status})`, codePreview, `    → ${truncate(resultText, 200)}`].join("\n");
    }
    if (name === "render_block") {
        const args = typeof callArgs === "object" ? callArgs : null;
        const specText = args?.spec ? "    spec: " + safeJson(args.spec) : "";
        return [`- render_block (${status})`, specText].filter(Boolean).join("\n");
    }
    if (name === "return_result") {
        const input = typeof callArgs === "object" ? callArgs : {};
        if (input.result_ref) return `- return_result (${status})  // result_ref=${input.result_ref}`;
        const valueText = safeJson(input.result);
        return `- return_result (${status})  // result=${truncate(valueText, 200)}`;
    }
    if (name?.startsWith("schema_")) {
        return `- ${name} (${status})  // schema lookup`;
    }
    if (name === "spawn_subagent" || name === "spawn_batch") {
        return `- ${name} (${status}) [id=${call.id}]  // nested — call inspect_subagent on this id for its inner trace`;
    }
    return `- ${name} (${status})  ${argText}`;
}

function extractResultText(toolResult) {
    const content = toolResult?.content;
    if (Array.isArray(content)) {
        const text = content.filter((c) => c?.type === "text").map((c) => c.text).join("");
        if (text) return text;
    }
    return "(no text content)";
}

// Lossless — these summaries are reproduction inputs, not budget-managed.
function formatChildArgs(input) {
    if (input == null) return "(no args)";
    if (typeof input === "string") return input;
    try {
        return JSON.stringify(input, null, 2);
    } catch {
        return String(input);
    }
}

// A child's analytics_query spec is load-bearing — the extractor lifts it
// verbatim into a kernel step — so this MUST be lossless. Compact (no
// indent collapses the empty stepFilter/subOrder/children arrays a
// traversal carries) but NEVER truncated; mirrors kernelSaveAgent's
// formatSpec. Context-budget management is compaction's job, not this.
function formatChildSpec(value) {
    if (value == null) return "(no args)";
    return typeof value === "string" ? value : safeJson(value);
}

function indent(text, leader) {
    if (!text) return "";
    const lines = text.split("\n");
    return leader + lines[0] + (lines.length > 1 ? "\n" + lines.slice(1).map((l) => " ".repeat(leader.length) + l).join("\n") : "");
}

function safeJson(value) {
    try { return JSON.stringify(value); } catch { return String(value); }
}

function truncate(text, max) {
    if (text == null) return "";
    const s = String(text);
    return s.length > max ? `${s.slice(0, max)}…` : s;
}

// If the child returned a `result_ref` (tool_call_id of row-shaped
// data it already fetched), look the rows up in the shared
// toolResultStore and re-record them under THIS spawn's tool_call_id
// so the parent has a stable handle it can pass into execute_python's
// `from_tool_calls`. Returns true on success — the caller appends the
// `_tool_call_id` footer to the tool result text in that case.
function promoteResultRef({ resultRef, toolCallId, namespaceKey, source }) {
    if (!resultRef || !toolCallId) return null;
    const stored = getResult(namespaceKey, resultRef);
    if (!stored || !Array.isArray(stored.rows)) return null;
    recordResult(namespaceKey, toolCallId, stored.rows, source);
    return { rowCount: stored.rows.length, originalSource: stored.source };
}

function summariseResult(result, hadSchema) {
    if (result == null) return "no result";
    if (typeof result === "string") return summarise(result);
    if (hadSchema && typeof result === "object") {
        const v = result.summary || result.title || result.verdict || result.label;
        if (typeof v === "string") return summarise(v);
        return summarise(JSON.stringify(result));
    }
    return summarise(JSON.stringify(result));
}

function itemId(item, idx) {
    if (item && typeof item === "object") {
        return String(item.id || item.name || item.key || idx);
    }
    return String(item ?? idx);
}
function itemLabel(item, idx) {
    const id = itemId(item, idx);
    return id.length > 12 ? id.slice(0, 12) : id;
}

// Lightweight {{path}} substitution. Supports {{item}} and dot-paths
// like {{item.id}} / {{item.name}}. Anything not found is left
// literal so the model can see what was missing.
function fillTemplate(template, item) {
    return String(template).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, path) => {
        const parts = path.split(".");
        let cur = parts[0] === "item" ? item : undefined;
        for (let i = 1; i < parts.length; i++) {
            if (cur == null) break;
            cur = cur[parts[i]];
        }
        if (cur == null) return `{{${path}}}`;
        if (typeof cur === "string") return cur;
        try { return JSON.stringify(cur); } catch { return String(cur); }
    });
}

// Trailing-edge throttled emit. Coalesces high-frequency event
// fan-out into one onUpdate per ~80ms; final state always lands via
// flush(). State is read via a getter so the snapshot always
// reflects the latest mutation.
function makeThrottledEmit(onUpdate, getState) {
    let scheduled = false;
    let dirty = false;
    const fire = () => {
        try { onUpdate?.({ details: snapshot(getState()) }); } catch { /* swallow */ }
    };
    const trigger = () => {
        if (scheduled) {
            dirty = true;
            return;
        }
        scheduled = true;
        fire();
        setTimeout(() => {
            scheduled = false;
            if (dirty) {
                dirty = false;
                trigger();
            }
        }, EMIT_INTERVAL_MS);
    };
    trigger.flush = () => {
        dirty = false;
        scheduled = false;
        fire();
    };
    return trigger;
}

// Top-level shallow copy + array dup so React sees a fresh details
// reference and re-renders the rows. Items inside are already fresh
// objects (we always replace via spread).
function snapshot(state) {
    if (!state) return state;
    if (state.kind === "batch") {
        return { ...state, items: state.items.slice() };
    }
    return { ...state, tools: (state.tools || []).slice() };
}

// Translate one pi-agent event into a delta on a state slot
// (subagent state directly, or a single batch item). The slot must
// have `tools[]`, `preview`, `activity` fields; both batch items and
// the single subagent state satisfy that contract.
export function forwardChildEvent(slot, event) {
    if (!slot) return;
    refreshMirror(slot);
    switch (event.type) {
        case "turn_start":
            slot.activity = "thinking…";
            slot.preview = "";
            return;
        case "tool_execution_start": {
            const name = event.toolName || "tool";
            slot.tools = [...(slot.tools || []), { name, status: "running", summary: "" }];
            slot.activity = `running ${name}…`;
            return;
        }
        case "tool_execution_end": {
            const tools = (slot.tools || []).slice();
            const last = tools.length ? tools[tools.length - 1] : null;
            if (last) {
                tools[tools.length - 1] = {
                    ...last,
                    status: event.isError ? "error" : "done",
                    summary: summariseToolResult(event.result, last.name) || (event.isError ? "error" : "done"),
                };
            }
            slot.tools = tools.length > 6 ? tools.slice(-6) : tools;
            slot.activity = event.isError ? "tool failed" : "thinking…";
            return;
        }
        case "message_update": {
            const inner = event.assistantMessageEvent;
            if (!inner) return;
            if (inner.type === "text_delta" && inner.delta) {
                slot.preview = ((slot.preview || "") + inner.delta).slice(-PREVIEW_TAIL);
                slot.activity = "writing…";
            } else if (inner.type === "thinking_delta" && inner.delta) {
                slot.preview = ((slot.preview || "") + inner.delta).slice(-PREVIEW_TAIL);
                slot.activity = "reasoning…";
            } else if (inner.type === "toolcall_start") {
                slot.activity = "preparing tool call…";
            }
            return;
        }
        default:
            return;
    }
}

function summariseToolResult(result, toolName) {
    if (!result) return "";
    const details = result.details;
    if (toolName === "analytics_query" && details && typeof details === "object") {
        const rows = Array.isArray(details.rows) ? details.rows.length : null;
        if (rows != null) return `${rows} row${rows === 1 ? "" : "s"}`;
    }
    if (toolName === "return_result") return "returned";
    if (toolName?.startsWith("schema_")) {
        if (Array.isArray(details?.entities)) return `${details.entities.length} entities`;
        if (Array.isArray(details?.matches)) return `${details.matches.length} matches`;
        if (details?.name) return details.name;
    }
    const text = result.content?.[0]?.text;
    if (typeof text === "string") return summarise(text);
    return "";
}
